import { getUserId } from "../../lib/effectiveAuthUser";
import React, { useEffect, useState, useRef } from 'react';
import { StyledContainer } from './styles';
import Textarea from "react-textarea-autosize";
import SVG from "react-inlinesvg";
import Shimmer from 'react-shimmer-effect';
import { makeId, sendAppEvent } from '../../Utils';
import SessionEvent from '../../models/SessionEvent';
import Auth from '../../Auth';
import classnames from 'classnames';
import { DEFAULT_WRITE_MODEL, WRITE_MODELS } from '../../../../src/shared/constants.js';
import { useSudowriteStore } from "src/stores/sudowriteStore";
import { UNLIMITED_EXPERIMENT_VARIATIONS } from '../../../../src/shared/constants.js';
import { useProjectStore } from "src/stores/projectStore";
import usePopoverClose from "../../hooks/usePopoverClose";
import Toggle from "../Toggle";

function LoadingItem({}) {
  return (
    <li className="loading-item">
      <div className="items">
        <Shimmer>
          <div className="first"></div>
        </Shimmer>

        <Shimmer>
          <div className="second"></div>
        </Shimmer>
      </div>
      <SVG
        className="next"
        src="/assets/right-chevron-rounded.svg"
      />
    </li>
  );
}

export default function Guidances({
  open,
  fetchGuidance,
  guidances,
  pluginOpenWormhole,
  loading,
  closeGuidance,
  automaticGuidances,
  setAutomaticGuidances,
  setGuidances,
  wormholeButtonRef,
  writeModel,
}) {
  const [custom, setCustom] = useState("");
  const isInUnlimitedExperiment = useSudowriteStore((state) => state.isInUnlimitedExperiment);
  const unlimitedExperimentVariation = useSudowriteStore((state) => state.unlimitedExperimentVariation);
  const hasAccessToPOVAndTenseBeta = useProjectStore((state) => state.hasAccessToPOVAndTenseBeta);
  const projectPovTense = useProjectStore((state) => state.povTense);
  const isMultiplePOV = useProjectStore((state) => state.isMultiplePOV.value);
  const currentDocumentPOVTense = useProjectStore((state) => state.currentDocumentPOVTense);
  const documentPOVTenseStatus = useProjectStore((state) => state.currentDocumentPOVTenseStatus);
  const retryDocumentPOVTense = useProjectStore((state) => state.retryDocumentPOVTense);
  const documentSettingsUnavailable = hasAccessToPOVAndTenseBeta && isMultiplePOV
    && documentPOVTenseStatus !== "ready";

  // if writeModel is not set, use the default write model, which may be basic if user is in unlimited experiment
  const finalWriteModel = writeModel || (isInUnlimitedExperiment && unlimitedExperimentVariation === UNLIMITED_EXPERIMENT_VARIATIONS.UNLIMITED_DEFAULT ? "basic" : DEFAULT_WRITE_MODEL);

  const { containerRef: wrapperRef, handleBlur } = usePopoverClose(open, closeGuidance, {
    refs: [wormholeButtonRef],
    onEscapeFocus: () => wormholeButtonRef.current?.focus(),
  });
  const sessionEvent = useRef();
  const textAreaRef = useRef(null);
  const wasOpenRef = useRef(false);
  const selectionRecordedRef = useRef(false);

  // get the guidance character limit is based on the write model
  const { guidanceCharacterLimit = 600 } = WRITE_MODELS.find((model) => model.value === finalWriteModel) || {};

  // tracks whether the custom field is focused
  const [customFocused, setCustomFocused] = useState(false);

  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      selectionRecordedRef.current = false;
      sessionEvent.current = new SessionEvent({
        sessionId: makeId(20),
        userId: getUserId(),
        type: "guidance",
      });

      sessionEvent.current.emit({
        name: "open",
      });

      // Focus the first interactive element when the panel opens
      const firstFocusable = wrapperRef.current?.querySelector('input, button, [tabindex="0"]');
      if (firstFocusable) {
        firstFocusable.focus();
      } else {
        wrapperRef.current?.focus();
      }
    }

    if (!open) {
      setCustomFocused(false);
    }
  }, [open]);

  const trackGuidedSelection = ({ guidanceSource, usedSuggestedGuidance, hasCustomGuidance, guidanceCharacterCount, action }) => {
    sendAppEvent("Write - Guided Selection", {
      writeTag: "Write - Guided",
      writeMode: "guided",
      guidanceSource,
      usedSuggestedGuidance,
      hasCustomGuidance,
      guidanceCharacterCount,
      suggestionsEnabled: automaticGuidances,
      action,
      guidanceSessionId: sessionEvent.current?.sessionId || null,
      writeModel: finalWriteModel || "unknown",
    });
    selectionRecordedRef.current = true;
  };

  useEffect(() => {
    if (!open && wasOpenRef.current && !selectionRecordedRef.current) {
      const trimmedCustom = custom.trim();
      const hasCustomGuidance = trimmedCustom.length > 0;
      trackGuidedSelection({
        guidanceSource: "neither",
        usedSuggestedGuidance: false,
        hasCustomGuidance,
        guidanceCharacterCount: trimmedCustom.length,
        action: "closed_without_selection",
      });
      wasOpenRef.current = false;
    }
  }, [open, automaticGuidances, finalWriteModel, custom]);

  const requestGuidance = () => {
    if (documentSettingsUnavailable) return;
    if (!hasAccessToPOVAndTenseBeta) {
      fetchGuidance();
    } else {
      fetchGuidance(isMultiplePOV ? currentDocumentPOVTense : projectPovTense);
    }
  };

  useEffect(() => {
    if (automaticGuidances && !guidances && !loading && open) {
      requestGuidance();
    } else if (!automaticGuidances) {
      setGuidances(null);
    }
  }, [automaticGuidances, open, hasAccessToPOVAndTenseBeta, isMultiplePOV, currentDocumentPOVTense, projectPovTense, documentSettingsUnavailable]);

  useEffect(() => {
    if (guidances) {
      sessionEvent.current.emit({
        name: "fetched_guidances",
        content: guidances.texts
          .map((guidance, i) => {
            return `${i + 1}. ${guidance}`;
          })
          .join("\n"),
        completionId: guidances.completionId,
      });
    }
  }, [guidances]);

  function submitCustom(custom) {
    const trimmedCustom = custom.trim();
    const hasCustomGuidance = trimmedCustom.length > 0;
    sessionEvent.current.emit({
      name: "choose_custom_guidance",
      content: custom,
    });
    trackGuidedSelection({
      guidanceSource: hasCustomGuidance ? "custom" : "neither",
      usedSuggestedGuidance: false,
      hasCustomGuidance,
      guidanceCharacterCount: trimmedCustom.length,
      action: "submitted_custom",
    });

    const nonce = makeId(15);
    pluginOpenWormhole(
      {
        guidance: custom,
        sessionId: sessionEvent.current.sessionId,
      },
      `custom_guidance_${nonce}`
    );
    closeGuidance();
  }

  return (
    <StyledContainer ref={wrapperRef} tabIndex={-1} role="dialog" aria-label="Guidance" onBlur={handleBlur}>
      <header>
        <h2>Tell Sudowrite what should come next</h2>
        <hr />
        <div className="row">
          <div className="toggle-setting">
            <Toggle set={setAutomaticGuidances} checked={automaticGuidances} id="suggestionsToggle" />
            <label htmlFor="suggestionsToggle">Suggestions</label>
          </div>

          {automaticGuidances && (
            <button
              type="button"
              className="refresh-btn"
              aria-label="Refresh suggestions"
              disabled={documentSettingsUnavailable}
              onClick={() => {
                if (documentSettingsUnavailable) return;
                sessionEvent.current.emit({
                  name: "click_refresh",
                });

                // TODO: handle case where user somehow changes cursor while dropdown is open -
                //       this would cause the refresh button to fire off suggested guidance at
                //       a different part of the document. we could cache the insertion point
                //       when the dropdown was open and always do it at the same spot
                requestGuidance();
              }}
            >
              <SVG
                className="reload"
                src="/assets/brainstorm/refresh.svg"
              />
            </button>
          )}
        </div>
      </header>
      <ul>
        <div className={classnames("suggested", { show: automaticGuidances })}>
          {automaticGuidances && documentSettingsUnavailable && (
            <div className="mx-6 mb-3 text-sm">
              <span role="status">{documentPOVTenseStatus === "error"
                ? "Could not load this document’s POV and tense."
                : "Loading POV and tense settings…"}</span>
              {documentPOVTenseStatus === "error" && (
                <button type="button" className="ml-2 underline" onClick={retryDocumentPOVTense}>Retry</button>
              )}
            </div>
          )}
          {!documentSettingsUnavailable && !loading &&
            guidances &&
            guidances.texts &&
            guidances.texts.map((guidance, i) => {
              return (
                <li
                  key={guidance}
                  tabIndex={0}
                  role="option"
                  onClick={(e) => {
                    e.preventDefault();

                    sessionEvent.current.emit({
                      name: "choose_guidance",
                      content: guidance,
                    });
                    trackGuidedSelection({
                      guidanceSource: "suggestion",
                      usedSuggestedGuidance: true,
                      hasCustomGuidance: false,
                      guidanceCharacterCount: guidance.trim().length,
                      action: "selected_suggestion",
                    });

                    // mark this guidance as chosen for the test
                    Auth.fetchWithIdToken("/api/test/metricEvent", {
                      experimentId: "suggested_guidance_test_2",
                      anonymousId: guidances.id,
                      type: "guidance_chosen",
                    });
                    const nonce = makeId(15);
                    pluginOpenWormhole(
                      {
                        guidance: guidance,
                        sessionId: sessionEvent.current.sessionId,
                      },
                      `suggested_guidance_${nonce}`
                    );

                    closeGuidance();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.currentTarget.click();
                    }
                  }}
                >
                  <p>{guidance}</p>
                  <SVG
                    className="next"
                    src="/assets/right-chevron-rounded.svg"
                  />
                </li>
              );
            })}

          {!documentSettingsUnavailable && loading && (
            <>
              <LoadingItem />
              <LoadingItem />
              <LoadingItem />
            </>
          )}
        </div>

        <hr />

        <li className="custom-guidance">
          {/* destroy and recreate the text area when we open and close or else a cursor will appear blinking on next open of guidance, even if the textarea isn't focused (not sure why!) */}
          {open && (
            <Textarea
              ref={textAreaRef}
              placeholder=""
              id="custom-guidance"
              value={custom}
              maxLength={guidanceCharacterLimit}
              maxRows={10}
              onChange={(e) => {
                setCustom(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.keyCode === 13) {
                  if (e.shiftKey) {
                    // Allow default behavior of adding a new line when Shift+Enter is pressed
                    return;
                  }
                  submitCustom(custom);
                }
              }}
              onFocus={(e) => {
                setCustomFocused(true);
              }}
              onBlur={(e) => {
                setCustomFocused(false);
              }}
            />
          )}

          {!customFocused && custom.length === 0 && (
            <p
              id="placeholder-text"
              onClick={(e) => {
                console.log("click");
                textAreaRef.current.focus();
              }}
            >
              Write your own.{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://docs.sudowrite.com/using-sudowrite/1ow1qkGqof9rtcyGnrWUBS/write/pvxUvbQqYybfEosqx1sXjY#%EF%B8%8F-write---guided"
              >
                See examples
              </a>
            </p>
          )}
          {custom.length > 0 && (
            <button
              id="done"
              onClick={(e) => {
                e.preventDefault();
                submitCustom(custom);
              }}
            >
              Go
            </button>
          )}
        </li>
      </ul>
    </StyledContainer>
  );
}
